import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, Button, Modal, Space, Tooltip } from "antd";
import { 
  DownloadOutlined, 
  DeleteOutlined, 
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  UserOutlined,
  CustomerServiceOutlined 
} from "@ant-design/icons";
import { descargarArchivo, eliminarArchivo } from "../../../../context/actions/solicitudesActions";
import dayjs from "dayjs";

export const FileItem = ({ archivo }) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.userReducer);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Determinar si es archivo propio
  const esArchivoPropio = archivo.usuarioID === user?.usuarioID;
  const esAdmin = archivo.usuarioNombre?.toLowerCase().includes('admin') || 
                 archivo.usuarioNombre?.toLowerCase().includes('soporte');

  // Obtener icono según tipo de archivo
  const getFileIcon = (nombreFile) => {
    const extension = nombreFile.split('.').pop()?.toLowerCase();
    const iconProps = { style: { fontSize: '20px', color: '#1890ff' } };
    
    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined {...iconProps} style={{ ...iconProps.style, color: '#ff4d4f' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileImageOutlined {...iconProps} style={{ ...iconProps.style, color: '#52c41a' }} />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined {...iconProps} style={{ ...iconProps.style, color: '#1890ff' }} />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined {...iconProps} style={{ ...iconProps.style, color: '#52c41a' }} />;
      case 'txt':
        return <FileTextOutlined {...iconProps} />;
      default:
        return <FileOutlined {...iconProps} />;
    }
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Tamaño desconocido';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDescargar = () => {
    dispatch(descargarArchivo(archivo.proveedorSolicitudDocumentoID, archivo.nombreFile));
  };

  const handleEliminar = () => {
    dispatch(eliminarArchivo(archivo.proveedorSolicitudDocumentoID, archivo.proveedorSolicitudID));
    setConfirmDelete(false);
  };

  return (
    <>
      <div className="file-card">
        <div className="file-card-content">
          <div className="file-left">
            <Avatar 
              size={32}
              icon={esAdmin ? <CustomerServiceOutlined /> : <UserOutlined />}
              style={{ 
                backgroundColor: esArchivoPropio ? '#ff7b00' : esAdmin ? '#52c41a' : '#1890ff',
                marginRight: 12
              }}
            />
            <div className="file-info">
              <div className="file-name" title={archivo.nombreFile}>
                {archivo.nombreFile}
              </div>
              <div className="file-meta">
                {archivo.usuarioNombre} • {dayjs(archivo.fechaCarga).format("DD/MM/YYYY HH:mm")}
              </div>
            </div>
          </div>
          
          <div className="file-actions">
            <Tooltip title="Descargar">
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={handleDescargar}
                size="small"
                className="action-btn"
              />
            </Tooltip>
            {esArchivoPropio && (
              <Tooltip title="Eliminar">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => setConfirmDelete(true)}
                  size="small"
                  danger
                  className="action-btn"
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <Modal
        title="¿Eliminar archivo?"
        open={confirmDelete}
        onOk={handleEliminar}
        onCancel={() => setConfirmDelete(false)}
        okText="Sí, eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <p>Esta acción eliminará permanentemente el archivo "{archivo.nombreFile}". ¿Deseas continuar?</p>
      </Modal>

      <style jsx>{`
        .file-card {
          background: #fff;
          border-radius: 8px;
          border: 1px solid #f0f0f0;
          margin-bottom: 8px;
          transition: all 0.2s ease;
        }
        
        .file-card:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          border-color: #d9d9d9;
        }
        
        .file-card-content {
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .file-left {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;
        }
        
        .file-info {
          flex: 1;
          min-width: 0;
        }
        
        .file-name {
          font-weight: 500;
          font-size: 14px;
          color: #333;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .file-meta {
          font-size: 12px;
          color: #999;
        }
        
        .file-actions {
          display: flex;
          gap: 4px;
          flex-shrink: 0;
        }
        
        .action-btn {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .action-btn:hover {
          background-color: #f5f5f5;
        }
        
        .action-btn.ant-btn-dangerous:hover {
          background-color: #fff2f0;
          border-color: #ffccc7;
        }
      `}</style>
    </>
  );
};